/**
 * Money. Extended amounts are integer paise, carried as `bigint`.
 *
 * The decision, stated once so nobody has to reconstruct it:
 *
 *   * An **extended amount** — anything that is a total — is a `Paise`, a branded
 *     `bigint`. The server column is `BigInteger`; `Number.MAX_SAFE_INTEGER` paise is
 *     about Rs 90,071,992,547,409.91, which a tenant with a large enough ledger can
 *     reach, and past it `JSON.parse` truncates without complaint. `bigint` cannot
 *     truncate and cannot accumulate float error, and TypeScript refuses to mix it with
 *     `number` in arithmetic — so a `paise * 1.18` typo is a compile error, not a
 *     rounding bug found by an auditor.
 *   * A **per-unit rate** is a `Dec` at 4 decimal places, because paise is 2dp and cannot
 *     hold a unit cost of Rs 12.3456 (D-15). It is not money in the paise sense and must
 *     not be stored as one.
 *   * The bridge between them is `extendedAmount`, which multiplies at FULL precision and
 *     rounds ONCE, mirroring `core/rounding.py::to_paise`. Rounding the rate first and
 *     then multiplying rounds twice and drifts — that is the specific bug the Python
 *     docstring warns about.
 */
import { Dec } from './decimal.js';
export const PAISE_PER_RUPEE = 100n;
export const ZERO_PAISE = 0n;
/** Assert that a bigint is already a paise amount. No conversion, no rounding. */
export function paise(units) {
    return units;
}
export class MoneyParseError extends Error {
    input;
    constructor(input) {
        super(`not an integer paise amount: ${JSON.stringify(String(input))}`);
        this.input = input;
        this.name = 'MoneyParseError';
    }
}
/**
 * Read a paise amount off the wire.
 *
 * Accepts a string (preferred — lossless at any magnitude) or a `number`, which is
 * rejected unless it is a safe integer. A `number` that is not a safe integer has already
 * lost digits by the time this function runs and there is nothing to recover; failing
 * loudly is the only honest option.
 */
export function parsePaise(raw) {
    if (typeof raw === 'bigint')
        return raw;
    if (typeof raw === 'number') {
        if (!Number.isSafeInteger(raw))
            throw new MoneyParseError(raw);
        return BigInt(raw);
    }
    const text = raw.trim();
    if (!/^[+-]?\d+$/.test(text))
        throw new MoneyParseError(raw);
    return BigInt(text);
}
/** Rupees (any precision) to integer paise. Rounds half away from zero, exactly once. */
export function toPaise(rupees) {
    return BigInt(Dec.parse(rupees).mul(100n).round(0).units);
}
/** Integer paise back to rupees, at 2dp. Exact — nothing is rounded here. */
export function fromPaise(amount) {
    return Dec.fromUnits(amount, 2);
}
/**
 * quantity x per-unit rate -> extended amount in paise.
 *
 * The product is taken at full precision and rounded once at the end. This is the
 * function every "stock value", "line total" and "COGS" figure in a skin must go through.
 */
export function extendedAmount(quantity, unitRate) {
    return toPaise(Dec.parse(quantity).mul(unitRate));
}
export function addPaise(...amounts) {
    return amounts.reduce((acc, a) => acc + a, 0n);
}
export function subPaise(a, b) {
    return (a - b);
}
export function negPaise(a) {
    return -a;
}
export function absPaise(a) {
    return (a < 0n ? -a : a);
}
export function comparePaise(a, b) {
    if (a < b)
        return -1;
    if (a > b)
        return 1;
    return 0;
}
//# sourceMappingURL=money.js.map